/**
 * @fileoverview One drafted entry under review (MOR-2335). Every field the
 * practitioner may correct is inline: matter (with candidates when the
 * evidence was ambiguous), category, units, narrative. Edits persist as
 * they are made so a reload or a rerun never loses them; Accept creates
 * the draft entry; Split and the merge checkbox reshape related work.
 *
 * Dependencies:
 * - Convex: timesheets.drafting.{updateSuggestion, acceptSuggestion,
 *   dismissSuggestion, splitSuggestion}
 */

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { format, parseISO } from "date-fns";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Scissors,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";
import { roundUnits } from "@cvx/timesheets/units";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Badge, Button } from "@/ui/primitives";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { NARRATIVE_INPUT_PROPS } from "./narrativeCheck";
import { NarrativeWarningNote } from "./NarrativeWarningNote";
import { DraftEvidenceList } from "./DraftEvidenceList";
import {
  BASIS_HINT,
  BASIS_LABEL,
  formatMinutes,
  type DraftSuggestion,
} from "./draftSuggestions.helpers";

const CONFIDENCE_BADGE: Record<DraftSuggestion["confidence"], string> = {
  high: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
  medium:
    "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
  low: "bg-muted text-muted-foreground",
};

export function DraftSuggestionRow({
  suggestion,
  orgId,
  fmtHours,
  unitMinutes,
  roundingMode,
  jobs,
  categories,
  selected,
  onSelectedChange,
}: {
  suggestion: DraftSuggestion;
  orgId: Id<"organizations">;
  fmtHours: (units: number) => string;
  /** Firm's unit size — the narrative prompt judges words against hours. */
  unitMinutes: number;
  /** MOR-1016 — firm's part-unit rounding direction for accepted units. */
  roundingMode: TimeRoundingMode;
  jobs: { _id: Id<"jobs">; name: string }[];
  categories: { _id: Id<"timeCategories">; name: string }[];
  /** Selection for merge and bulk dismiss; undefined hides the checkbox. */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}) {
  const acceptSuggestion = useMutation(
    api.timesheets.drafting.acceptSuggestion,
  );
  const dismissSuggestion = useMutation(
    api.timesheets.drafting.dismissSuggestion,
  );
  const updateSuggestion = useMutation(
    api.timesheets.drafting.updateSuggestion,
  );
  const splitSuggestion = useMutation(api.timesheets.drafting.splitSuggestion);

  // Inline-editable copy — deltas vs the suggestion persist as edits.
  const [jobId, setJobId] = useState<string>(suggestion.jobId ?? "");
  const [categoryId, setCategoryId] = useState<string>(
    suggestion.categoryId ?? "",
  );
  const [units, setUnits] = useState(String(suggestion.units));
  const [narrative, setNarrative] = useState(suggestion.narrative);
  const [showEvidence, setShowEvidence] = useState(false);
  const [busy, setBusy] = useState(false);

  // A merge or rerun can replace the row's data under us — follow it.
  useEffect(() => {
    setJobId(suggestion.jobId ?? "");
    setCategoryId(suggestion.categoryId ?? "");
    setUnits(String(suggestion.units));
    setNarrative(suggestion.narrative);
  }, [
    suggestion._id,
    suggestion.jobId,
    suggestion.categoryId,
    suggestion.units,
    suggestion.narrative,
  ]);

  const persist = async (
    patch: Parameters<typeof updateSuggestion>[0]["patch"],
  ) => {
    try {
      await updateSuggestion({ suggestionId: suggestion._id, patch });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the change."));
    }
  };

  const canAccept =
    !!categoryId && !!narrative.trim() && Number(units) >= 1 && !busy;

  const handleAccept = async () => {
    if (!canAccept) return;
    setBusy(true);
    try {
      const overrides: NonNullable<
        Parameters<typeof acceptSuggestion>[0]["overrides"]
      > = {};
      if (jobId && jobId !== suggestion.jobId)
        overrides.jobId = jobId as Id<"jobs">;
      if (categoryId !== suggestion.categoryId) {
        overrides.categoryId = categoryId as Id<"timeCategories">;
      }
      const u = roundUnits(Number(units), roundingMode);
      if (u !== suggestion.units) overrides.units = u;
      if (narrative.trim() !== suggestion.narrative)
        overrides.narrative = narrative.trim();
      await acceptSuggestion({
        suggestionId: suggestion._id,
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });
      toast.success("Entry added to your timesheet as a draft.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not accept the entry."));
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setBusy(true);
    try {
      await dismissSuggestion({ suggestionId: suggestion._id });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not dismiss the entry."));
      setBusy(false);
    }
  };

  const handleSplit = async () => {
    const total = Math.max(2, Math.round(Number(units)) || suggestion.units);
    setBusy(true);
    try {
      await splitSuggestion({
        suggestionId: suggestion._id,
        firstUnits: Math.max(1, Math.floor(total / 2)),
      });
      toast.success("Split into two entries — allocate the time between them.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not split the entry."));
      setBusy(false);
    }
  };

  const jobOptions =
    suggestion.candidateJobs.length > 0 && !suggestion.jobId
      ? suggestion.candidateJobs.map((c) => ({ _id: c.jobId, name: c.name }))
      : jobs;
  const showAllJobs =
    suggestion.candidateJobs.length > 0 && !suggestion.jobId && jobs.length > 0;

  return (
    <div
      className="flex flex-col gap-2 px-4 py-3"
      data-testid="draft-suggestion-row"
    >
      <div className="flex flex-wrap items-center gap-2">
        {selected !== undefined && onSelectedChange && (
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelectedChange(v === true)}
            aria-label="Select suggestion"
          />
        )}
        <span className="text-xs font-medium tabular-nums text-muted-foreground">
          {format(parseISO(suggestion.date), "EEE d MMM")}
        </span>
        <Badge className={cn("gap-1", CONFIDENCE_BADGE[suggestion.confidence])}>
          <Sparkles className="size-3" />
          AI · {suggestion.confidence}
        </Badge>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Badge
                  color={
                    suggestion.durationBasis === "scheduled" ? "amber" : "gray"
                  }
                  variant="soft"
                  size="sm"
                  aria-label={`${BASIS_LABEL[suggestion.durationBasis]}. ${BASIS_HINT[suggestion.durationBasis]}`}
                >
                  {BASIS_LABEL[suggestion.durationBasis]}
                  {suggestion.scheduledMinutes !== undefined &&
                    ` · booked ${formatMinutes(suggestion.scheduledMinutes)}`}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              {BASIS_HINT[suggestion.durationBasis]}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {suggestion.jobMatch === "unresolved" && (
          <Badge color="amber" variant="outline" size="sm">
            Matter not identified
          </Badge>
        )}
        {suggestion.jobMatch === "candidate" && (
          <Badge color="amber" variant="outline" size="sm">
            Matter uncertain
          </Badge>
        )}
        {suggestion.projectCode === "pre" && (
          <Badge className="bg-muted text-muted-foreground">PRE</Badge>
        )}
        {!suggestion.billable && (
          <Badge className="bg-muted text-muted-foreground">Non-billable</Badge>
        )}
        {suggestion.editedAt !== undefined && (
          <Badge color="blue" variant="soft" size="sm">
            Edited
          </Badge>
        )}
        <span className="text-xs tabular-nums text-muted-foreground">
          {fmtHours(suggestion.units)}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            className="h-7"
            disabled={!canAccept}
            onClick={() => void handleAccept()}
          >
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
            Accept
          </Button>
          <div className="flex items-center rounded-md border border-border">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-muted-foreground"
              disabled={busy || suggestion.units < 2}
              onClick={() => void handleSplit()}
              title="Split into two entries"
            >
              <Scissors className="size-3.5" />
              Split
            </Button>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground"
                disabled={busy}
                aria-label="Dismiss suggestion"
              >
                <X className="size-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Dismiss this suggestion?</AlertDialogTitle>
                <AlertDialogDescription>
                  It will be removed from your review and will not be added to
                  your timesheet. The same activity will not be suggested again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => void handleDismiss()}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Dismiss
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {suggestion.overlaps.length > 0 && (
        <p
          className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
          role="status"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            Overlaps in time with{" "}
            {suggestion.overlaps
              .map(
                (o) =>
                  `${o.kind === "entry" ? "your entry" : "the suggestion"} “${o.label}”`,
              )
              .join(", ")}
            . The times are not added together — check which is right.
          </span>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={jobId}
          onValueChange={(value) => {
            setJobId(value);
            void persist({ jobId: value as Id<"jobs"> });
          }}
        >
          <SelectTrigger
            className={cn("h-8 w-64 max-w-full", !jobId && "border-amber-400")}
            aria-label="Matter"
          >
            <SelectValue
              placeholder={
                suggestion.candidateJobs.length > 0
                  ? "Choose between the possible matters…"
                  : "Pick a matter…"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {jobOptions.map((j) => (
              <SelectItem key={j._id} value={j._id}>
                {j.name}
              </SelectItem>
            ))}
            {showAllJobs &&
              jobs
                .filter(
                  (j) =>
                    !suggestion.candidateJobs.some((c) => c.jobId === j._id),
                )
                .map((j) => (
                  <SelectItem key={j._id} value={j._id}>
                    {j.name}
                  </SelectItem>
                ))}
          </SelectContent>
        </Select>
        <Select
          value={categoryId}
          onValueChange={(value) => {
            setCategoryId(value);
            void persist({ categoryId: value as Id<"timeCategories"> });
          }}
        >
          <SelectTrigger
            className={cn(
              "h-8 w-44 max-w-full",
              !categoryId && "border-amber-400",
            )}
            aria-label="Category"
          >
            <SelectValue placeholder="Category…" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c._id} value={c._id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="number"
          min={1}
          step={1}
          value={units}
          onChange={(e) => setUnits(e.target.value)}
          onBlur={() => {
            const u = roundUnits(Number(units), roundingMode);
            if (Number.isFinite(u) && u >= 1 && u !== suggestion.units) {
              void persist({ units: u });
            }
          }}
          className="h-8 w-20 tabular-nums"
          aria-label="Units"
        />
        <Input
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          onBlur={() => {
            const next = narrative.trim();
            if (next && next !== suggestion.narrative)
              void persist({ narrative: next });
          }}
          className="h-8 min-w-64 flex-1"
          aria-label="Narrative"
          {...NARRATIVE_INPUT_PROPS}
        />
        <NarrativeWarningNote
          narrative={narrative}
          units={Number(units)}
          unitMinutes={unitMinutes}
          orgId={orgId}
          className="w-full"
        />
      </div>

      <p className="text-xs text-muted-foreground" data-testid="duration-note">
        {suggestion.durationNote}
      </p>
      {suggestion.reason && (
        <p className="text-xs text-muted-foreground">{suggestion.reason}</p>
      )}

      <button
        type="button"
        onClick={() => setShowEvidence((s) => !s)}
        className="flex items-center gap-1 self-start text-xs text-muted-foreground hover:text-foreground"
        aria-expanded={showEvidence}
      >
        {showEvidence ? (
          <ChevronDown className="size-3" />
        ) : (
          <ChevronRight className="size-3" />
        )}
        Based on {suggestion.evidence.length}{" "}
        {suggestion.evidence.length === 1 ? "activity" : "activities"}
      </button>
      {showEvidence && (
        <DraftEvidenceList evidence={suggestion.evidence} className="pl-4" />
      )}
    </div>
  );
}
